<template>
  <div id="document-pending-list">
    <vs-alert color="success" v-if="pendingDocuments.length === 0">
      {{ $t('admin.not_pending_msg') }}
    </vs-alert>
    <div v-else>
      <div class="flex flex-wrap-reverse items-center flex-grow justify-between mb-base">
        <!-- ITEMS PER PAGE -->
        <vs-dropdown vs-trigger-click class="cursor-pointer">
          <div
            class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
          >
            <span class="mr-2">
              {{ (currentPage * pageSize - (pageSize - 1)) | persian_digits }} {{ $t('forms.to') }}
              {{
                documentDataCount - currentPage * pageSize > 0
                  ? currentPage * pageSize
                  : documentDataCount | persian_digits
              }}
              {{ $t('forms.from') }} {{ documentDataCount | persian_digits }}
            </span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
          </div>
          <vs-dropdown-menu>
            <vs-dropdown-item @click="pageSize = 5">
              <span>{{ 5 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 10">
              <span>{{ 10 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 50">
              <span>{{ 50 | persian_digits }}</span>
            </vs-dropdown-item>
            <vs-dropdown-item @click="pageSize = 100">
              <span>{{ 100 | persian_digits }}</span>
            </vs-dropdown-item>
          </vs-dropdown-menu>
        </vs-dropdown>
        <!-- PAGINATION -->
        <div>
          <vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
        </div>
      </div>
      <div v-for="document in documentData" :key="document.id">
        <vx-card class="mb-base" :label="document.id">
          <div class="vx-row">
            <div class="vx-col" id="avatar-col">
              <div class="img-container mb-4">
                <avatar :id="document.user.avatar" size="50px" class="mr-1" />
              </div>
            </div>
            <div class="vx-col flex-1 mt-2">
              <div class="font-semibold">{{ $t('users.user') }}</div>
              <div>{{ document.user.details.firstName }} {{ document.user.details.lastName }}</div>
            </div>
            <div class="vx-col" v-if="['pending', 'unspecified'].includes(document.state)">
              <vs-button
                size="small"
                color="success"
                icon="icon-check"
                icon-pack="feather"
                class="w-full"
                type="flat"
                @click.stop="openApprovePrompt(document)"
              >
                {{ $t('admin.confirm_pending') }}
              </vs-button>
              <vs-button
                size="small"
                color="danger"
                icon="icon-x"
                icon-pack="feather"
                class="w-full"
                type="flat"
                @click.stop="openRejectPrompt(document)"
              >
                {{ $t('admin.reject_pending') }}
              </vs-button>
            </div>
          </div>
          <vx-card no-shadow card-border class="mb-base" v-if="document.state === 'approved'">
            <vs-row>
              <div class="vx-col mb-3 w-full md:w-1/3" v-if="document.expirationDate">
                <vs-row class="font-semibold">{{ $t('documents.expiration_date') }}</vs-row>
                <vs-row class="mt-3">{{ document.expirationDate | datetime }}</vs-row>
              </div>
              <div class="vx-col mb-3 w-full md:w-2/3" v-if="document.approvedSubjects.length">
                <vs-row class="font-semibold">{{ $t('documents.approved_subjects') }}</vs-row>
                <vs-row class="mt-3">
                  <span v-for="subject in document.approvedSubjects" :key="subject" class="pl-2">
                    &#9679; {{ $t(`places.subjects.${subject}`) }}
                  </span>
                </vs-row>
              </div>
            </vs-row>
          </vx-card>
          <vx-card
            no-shadow
            card-border
            class="mb-base"
            content-color="danger"
            v-if="document.state === 'rejected'"
          >
            <vs-row>
              <div class="vx-col mb-3 w-full">
                <vs-row class="font-semibold">{{ $t('documents.reject_message') }}</vs-row>
                <vs-row class="mt-3">{{ document.rejectMessage }}</vs-row>
              </div>
            </vs-row>
          </vx-card>
          <div class="vx-row">
            <div class="vx-col mb-3 w-full md:w-1/5">
              <h4>{{ $t('documents.financial_info') }}</h4>
            </div>
            <div class="vx-col mb-3 w-full md:w-4/5">
              <vs-row>
                <div class="vx-col mb-3 w-full md:w-1/3">
                  <vs-row class="font-semibold">{{ $t('documents.bank_name') }}</vs-row>
                  <vs-row class="mt-3">{{ document.bankName }}</vs-row>
                </div>
                <div class="vx-col mb-3 w-full md:w-1/3">
                  <vs-row class="font-semibold">{{ $t('documents.branch_no') }}</vs-row>
                  <vs-row class="mt-3">{{ document.branchNo }}</vs-row>
                </div>
                <div class="vx-col mb-3 w-full">
                  <vs-row class="font-semibold">{{ $t('documents.iban') }}</vs-row>
                  <vs-row class="mt-3">{{ document.IBAN }}</vs-row>
                </div>
                <div class="vx-col mb-3 w-full">
                  <vs-row class="font-semibold">{{ $t('documents.credit_card') }}</vs-row>
                  <vs-row class="mt-3">{{ document.creditCard }}</vs-row>
                </div>
              </vs-row>
            </div>
            <div class="vx-col mb-3 w-full md:w-1/5">
              <h4>{{ $t('documents.docs_and_grades') }}</h4>
            </div>
            <div class="vx-col mb-3 w-full md:w-4/5">
              <vs-row>
                <div class="vx-col mb-3 w-full md:w-1/3">
                  <vs-row class="font-semibold">{{ $t('documents.grade') }}</vs-row>
                  <vs-row class="mt-3">{{ document.grade }}</vs-row>
                </div>
                <div class="vx-col mb-3 w-full md:w-1/3">
                  <vs-row class="font-semibold">{{ $t('documents.field_of_study') }}</vs-row>
                  <vs-row class="mt-3">{{ document.fieldOfStudy }}</vs-row>
                </div>
                <div class="vx-col mb-3 w-full md:w-1/3">
                  <vs-row class="font-semibold">{{ $t('documents.license_no') }}</vs-row>
                  <vs-row class="mt-3">{{ document.licenseNo.join(', ') }}</vs-row>
                </div>
                <div class="vx-col mb-3 w-full" v-if="document.description">
                  <vs-row class="font-semibold">{{ $t('documents.description') }}</vs-row>
                  <vs-row class="mt-3">{{ document.description }}</vs-row>
                </div>
                <download-files class="mb-base" :files="document.attachments" />
              </vs-row>
            </div>
          </div>
        </vx-card>
      </div>
    </div>
    <vs-popup :active.sync="activeApprovePrompt" :title="$t('admin.confirm_pending')" class="approve-popup">
      <div class="w-full mb-base">
        <label class="vs-input--label">{{ $t('documents.expiration_date') }}</label>
        <date-picker
          type="date"
          v-model="expirationDate"
          element="expirationDate"
          :min="$moment().format()"
        />
        <vs-input
          name="expirationDate"
          id="expirationDate"
          :value="
            $moment(expirationDate)
              .locale($vs.rtl ? 'fa' : 'en')
              .format('DD MMMM YYYY')
          "
          v-validate="'required'"
          class="w-full"
        />
        <span class="text-danger text-sm" v-show="errors.has('expirationDate')">{{
          errors.first('expirationDate')
        }}</span>
      </div>
      <div class="w-full mb-base">
        <label class="vs-input--label">{{ $t('documents.approved_subjects') }}</label>
        <v-select
          v-model="approvedSubjects"
          :clearable="false"
          :options="subjectOptions"
          :reduce="option => option.value"
          name="approvedSubjects"
          :dir="$vs.rtl ? 'rtl' : 'ltr'"
          class="w-full"
          multiple
        />
        <span class="text-danger text-sm" v-show="errors.has('approvedSubjects')">{{
          errors.first('approvedSubjects')
        }}</span>
      </div>

      <vs-button color="success" icon="icon-check" icon-pack="feather" @click.stop="approveDocument">
        {{ $t('admin.confirm_pending') }}
      </vs-button>
    </vs-popup>
    <vs-popup :active.sync="activeRejectPrompt" :title="$t('admin.reject_pending')">
      <div class="w-full mb-base">
        <label class="vs-input--label">{{ $t('documents.reject_message') }}</label>
        <vs-textarea
          v-model="rejectMessage"
          name="rejectMessage"
          id="rejectMessage"
          v-validate="'required'"
          class="w-full"
        />
        <span class="text-danger text-sm" v-show="errors.has('rejectMessage')">{{
          errors.first('rejectMessage')
        }}</span>
      </div>
      <vs-button color="danger" icon="icon-check" icon-pack="feather" @click.stop="rejectDocument">
        {{ $t('admin.reject_pending') }}
      </vs-button>
    </vs-popup>
  </div>
</template>

<script>
import moduleDocument from '@/store/document/moduleDocument'
import moduleUserManagement from '@/store/user-management/moduleUserManagement'
import Avatar from '@/views/components/common/Avatar'
import DownloadFiles from '@/views/components/forms/DownloadFiles'

export default {
  name: 'DocumentPendingList',
  components: {
    DownloadFiles,
    Avatar
  },
  data() {
    return {
      pendingDocuments: [],
      pageSize: 10,
      skip: 0,
      categories: [],
      activeApprovePrompt: false,
      activeRejectPrompt: false,
      selectedDocument: {},
      expirationDate: this.$moment()
        .add(1, 'jYear')
        .format(),
      approvedSubjects: null,
      rejectMessage: null
    }
  },
  methods: {
    approveDocument() {
      this.$vs.loading()
      this.$store
        .dispatch('document/editDocumentState', {
          id: this.selectedDocument.id,
          data: {
            state: 'approved',
            expirationDate: this.expirationDate,
            approvedSubjects: this.approvedSubjects
          }
        })
        .then(() => {
          this.selectedDocument.state = 'approved'
          this.selectedDocument.expirationDate = this.expirationDate
          this.selectedDocument.approvedSubjects = this.approvedSubjects
          this.activeApprovePrompt = false
        })
        .finally(() => this.$vs.loading.close())
    },
    rejectDocument() {
      this.$vs.loading()
      this.$store
        .dispatch('document/editDocumentState', {
          id: this.selectedDocument.id,
          data: {
            state: 'rejected',
            rejectMessage: this.rejectMessage
          }
        })
        .then(() => {
          this.selectedDocument.state = 'rejected'
          this.selectedDocument.rejectMessage = this.rejectMessage
          this.activeRejectPrompt = false
        })
        .finally(() => this.$vs.loading.close())
    },
    downloadFile(fileId) {
      this.$vs.loading({
        container: `#download-${fileId}`,
        scale: 0.2
      })
      this.$store
        .dispatch('file/downloadFile', fileId)
        .then(response => {
          window.open(response, '_blank')
          this.$vs.loading.close(`#download-${fileId} > .con-vs-loading`)
        })
        .catch(() => this.$vs.loading.close(`#download-${fileId} > .con-vs-loading`))
    },
    openApprovePrompt(document) {
      this.selectedDocument = document
      this.activeApprovePrompt = true
    },
    openRejectPrompt(document) {
      this.selectedDocument = document
      this.activeRejectPrompt = true
    }
  },
  computed: {
    documentDataCount() {
      return this.pendingDocuments.length
    },
    documentData() {
      return this.pendingDocuments.slice(this.skip, this.skip + this.pageSize)
    },
    totalPages() {
      return Math.ceil(this.pendingDocuments.length / this.pageSize)
    },
    currentPage: {
      get() {
        return Math.ceil(this.skip / this.pageSize + 1)
      },
      set(val) {
        this.skip = (val - 1) * this.pageSize
      }
    },
    subjectOptions() {
      const cats = this.categories
      const options = []
      for (const [cKey, cat] of Object.entries(cats)) {
        for (const r in cat) {
          options.push({
            label: this.$t(`places.categories.${cKey}`) + ' - ' + this.$t(`places.subjects.${cat[r]}`),
            value: cat[r]
          })
        }
      }
      return options
    }
  },
  created() {
    this.$vs.loading()
    if (!moduleDocument.isRegistered) {
      this.$store.registerModule('document', moduleDocument)
      moduleDocument.isRegistered = true
    }
    if (!moduleUserManagement.isRegistered) {
      this.$store.registerModule('userManagement', moduleUserManagement)
      moduleUserManagement.isRegistered = true
    }

    this.$store.dispatch('config/fetchConfig', 'Place:Category:Subject').then(res => {
      this.categories = res.data.value
    })

    this.$store
      .dispatch('document/fetchDocuments', { query: { state: 'pending' } })
      .then(res => {
        const data = JSON.parse(JSON.stringify(res.data))
        if (data.length === 0) {
          this.$vs.loading.close()
          this.pendingDocuments = []
        }
        let documents = []
        let count = 0
        data.forEach(document => {
          this.$store
            .dispatch('userManagement/fetchUser', document.userId)
            .then(user => {
              documents.push({ ...document, user: user.data })
              count += 1
              if (count === data.length) {
                this.pendingDocuments = documents
                this.$vs.loading.close()
              }
            })
            .catch(() => this.$vs.loading.close())
        })
      })
      .catch(() => this.$vs.loading.close())
  }
}
</script>

<style>
.vs__dropdown-toggle {
  height: auto !important;
  min-height: 44px;
}

.approve-popup .vs-popup--content {
  overflow: visible;
}
</style>
